import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import Toggle from '../components/Toggle';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import {
  Listing,
  ListingInput,
  ListingQuota,
  createListing,
  getListing,
  getListingQuota,
  listingTypeLabel,
  updateListing,
} from '../api/listings';
import { uploadListingPhotos } from '../api/uploads';
import { extractApiError } from '../api/legalServices';

const STEPS = ['Basics', 'Details', 'Photos', 'Review'];

/** Hard limit on photos per listing (enforced on the backend too). */
const MAX_PHOTOS = 10;

const AMENITIES = [
  'Central AC', 'In-Unit Laundry', 'Dishwasher', 'Parking Included',
  'Hardwood Floors', 'Balcony/Patio', 'Pet Friendly', 'Gym Access',
  'Pool', 'Storage Unit', 'EV Charging', 'Doorman',
  'Elevator', 'Fireplace', 'Smart Home',
];

interface FormState {
  title: string;
  address: string;
  type: 'Rental' | 'Sale';
  price: string;
  beds: number;
  baths: number;
  sqft: string;
  available: string;
  status: 'active' | 'inactive';
  description: string;
  amenities: string[];
  petPolicy: string;
  lease: string;
}

const INITIAL: FormState = {
  title: '',
  address: '',
  type: 'Rental',
  price: '',
  beds: 1,
  baths: 1,
  sqft: '',
  available: '',
  status: 'active',
  description: '',
  amenities: ['Central AC', 'In-Unit Laundry'],
  petPolicy: 'allowed',
  lease: '1-year',
};

/** Build editable form state from a Listing returned by the backend. */
const formFromListing = (l: Listing): FormState => {
  const priceNum = typeof l.price === 'number' ? l.price : Number(l.price);
  return {
    title: l.title ?? '',
    address: l.address ?? '',
    type: l.type ?? 'Rental',
    price: Number.isFinite(priceNum) && priceNum > 0 ? String(priceNum) : '',
    beds: l.beds ?? 0,
    baths: l.baths ?? 0,
    sqft: l.sqft != null ? String(l.sqft) : '',
    // Backend ships DATEONLY as 'YYYY-MM-DD' which an <input type="date"> accepts.
    available: l.availableFrom ?? '',
    // The backend enum is exactly { active, inactive }, so this is 1:1.
    status: l.status === 'active' ? 'active' : 'inactive',
    description: l.description ?? '',
    amenities: Array.isArray(l.amenities) ? l.amenities : [],
    petPolicy: l.petPolicy ?? 'allowed',
    lease: l.leaseTerm ?? '1-year',
  };
};

/**
 * Shared add/edit listing wizard, mounted under both panels:
 *   /agent/listings/new (+ /:id/edit) and /admin/listings/new.
 * Either role creates the listing as themselves.
 */
const AgentAddListing: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  const listingsPath = isAdmin ? '/admin/listings' : '/agent/listings';
  const { id: idParam } = useParams<{ id?: string }>();
  const editingId = idParam ? Number(idParam) : null;
  const isEditing = editingId !== null && Number.isFinite(editingId);
  const { user } = useAuth();
  /*
   * Only an admin picks a listing's publish state. An agent's new submission
   * always starts inactive and waits for an admin to activate it, so the
   * publish toggle is hidden on the agent's create flow entirely — showing it
   * would promise a "live" listing the backend never grants
   * (listing.controller.js forces status='inactive' for non-admin creates).
   */
  const canChooseStatus = user?.role === 'admin';
  const submitsForReview = !isEditing && !canChooseStatus;
  const { toast } = useToast();
  const [step, setStep] = useState<number>(1);
  const [form, setForm] = useState<FormState>(INITIAL);
  // Photos now hold real URLs returned by /api/uploads/listing-photos.
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  // Set true while we're fetching the existing listing in edit mode.
  const [loadingExisting, setLoadingExisting] = useState<boolean>(isEditing);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Quota gate — only applies on the create flow, not when editing.
  const [quota, setQuota] = useState<ListingQuota | null>(null);
  const [checkingQuota, setCheckingQuota] = useState<boolean>(!isEditing);
  const [quotaError, setQuotaError] = useState<string | null>(null);

  // Inline field errors, keyed by field name.
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // On phones the step buttons sit far below the fold — start each step at the top.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  // ─── Fetch existing listing when in edit mode ────────────────────────
  useEffect(() => {
    if (!isEditing || editingId === null) {
      setLoadingExisting(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingExisting(true);
      setLoadError(null);
      try {
        const listing = await getListing(editingId);
        if (cancelled) return;
        setForm(formFromListing(listing));
        setPhotos(Array.isArray(listing.photos) ? listing.photos : []);
      } catch (err) {
        if (cancelled) return;
        setLoadError(extractApiError(err, 'Failed to load listing'));
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditing, editingId]);

  // ─── Check the quota on Step 1 of the create flow ───────────────────
  // Editing existing listings doesn't consume a new slot, so we skip the
  // check entirely in edit mode.
  useEffect(() => {
    if (isEditing) {
      setCheckingQuota(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setCheckingQuota(true);
      setQuotaError(null);
      try {
        const q = await getListingQuota();
        if (!cancelled) setQuota(q);
      } catch (err) {
        if (!cancelled)
          setQuotaError(extractApiError(err, 'Failed to check your plan limit'));
      } finally {
        if (!cancelled) setCheckingQuota(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditing]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Clear the inline error for this field as soon as the user edits it.
    setErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key as string];
      return next;
    });
  };

  const toggleAmenity = (a: string) =>
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a)
        ? f.amenities.filter((x) => x !== a)
        : [...f.amenities, a],
    }));

  /** Per-step validators. Return a map of fieldName -> error message. */
  const validateStep1 = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!form.title.trim()) {
      errs.title = 'Listing title is required.';
    } else if (form.title.trim().length < 3) {
      errs.title = 'Title must be at least 3 characters.';
    } else if (form.title.trim().length > 200) {
      errs.title = 'Title must be 200 characters or fewer.';
    }

    if (!form.address.trim()) {
      errs.address = 'Full address is required.';
    } else if (form.address.trim().length < 5) {
      errs.address = 'Please enter a complete address.';
    }

    if (form.price.trim() === '') {
      errs.price = 'Price is required.';
    } else {
      const n = Number(form.price);
      if (!Number.isFinite(n)) {
        errs.price = 'Price must be a number.';
      } else if (n <= 0) {
        errs.price = 'Price must be greater than 0.';
      }
    }

    if (form.sqft.trim() !== '') {
      const n = Number(form.sqft);
      if (!Number.isFinite(n) || n <= 0) {
        errs.sqft = 'Square footage must be a positive number.';
      }
    }

    if (form.beds < 0) errs.beds = 'Bedrooms cannot be negative.';
    if (form.baths < 0) errs.baths = 'Bathrooms cannot be negative.';

    if (form.available) {
      const d = new Date(form.available);
      if (Number.isNaN(d.getTime())) {
        errs.available = 'Please pick a valid date.';
      }
    }

    return errs;
  };

  const validateStep2 = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!form.description.trim()) {
      errs.description = 'Description is required.';
    } else if (form.description.trim().length < 10) {
      errs.description = 'Description should be at least 10 characters.';
    } else if (form.description.length > 1000) {
      errs.description = 'Description must be 1000 characters or fewer.';
    }
    if (form.amenities.length === 0) {
      errs.amenities = 'Pick at least one amenity.';
    }
    return errs;
  };

  const validateStep3 = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (photos.length === 0) {
      errs.photos = 'Please upload at least one photo.';
    }
    return errs;
  };

  const validateAll = (): Record<string, string> => ({
    ...validateStep1(),
    ...validateStep2(),
    ...validateStep3(),
  });

  /**
   * Step navigation. Moving forward runs the current step's validator and
   * blocks the transition (with inline errors + a toast) if anything fails.
   * Moving backward never blocks.
   */
  const goToStep = (target: number) => {
    if (target < step) {
      setErrors({});
      setStep(target);
      return;
    }

    let stepErrors: Record<string, string> = {};
    if (step === 1) stepErrors = validateStep1();
    else if (step === 2) stepErrors = validateStep2();
    else if (step === 3) stepErrors = validateStep3();

    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      toast('Please fix the highlighted fields before continuing.', 'error');
      return;
    }
    setErrors({});
    setStep(target);
  };

  /** Upload one or more selected/dropped files and append the URLs to state. */
  const handleFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) {
      toast('Please pick image files (JPG, PNG, WEBP).', 'error');
      return;
    }
    // Hard cap: a listing can have at most MAX_PHOTOS images in total.
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast(`Maximum ${MAX_PHOTOS} images can be uploaded.`, 'error');
      return;
    }
    const slice = files.slice(0, remaining);
    if (slice.length < files.length) {
      toast(
        `Maximum ${MAX_PHOTOS} images can be uploaded — only the first ${slice.length} ${
          slice.length === 1 ? 'file was' : 'files were'
        } added.`,
        'error'
      );
    }

    setUploading(true);
    try {
      const urls = await uploadListingPhotos(slice);
      setPhotos((prev) => [...prev, ...urls]);
      toast(`${urls.length} photo${urls.length === 1 ? '' : 's'} uploaded`, 'success');
    } catch (err) {
      toast(extractApiError(err, 'Upload failed'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const onPickFiles = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    // Reset so the same file can be re-picked after deletion.
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (uploading) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removePhoto = (idx: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const publish = async () => {
    // ── Final guard: run every step's validator one more time ────────
    const allErrors = validateAll();
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      // Jump back to the first step that contains an offending field so
      // the user can see what to fix.
      const step1Keys = ['title', 'address', 'price', 'sqft', 'beds', 'baths', 'available'];
      const step2Keys = ['description', 'amenities'];
      if (step1Keys.some((k) => allErrors[k])) setStep(1);
      else if (step2Keys.some((k) => allErrors[k])) setStep(2);
      else if (allErrors.photos) setStep(3);
      toast('Please fix the highlighted fields before publishing.', 'error');
      return;
    }

    // ── Build the payload, mapping the form's shape to the backend's ─
    const payload: ListingInput = {
      title: form.title.trim(),
      address: form.address.trim(),
      type: form.type,
      price: form.price === '' ? 0 : Number(form.price),
      beds: form.beds,
      baths: form.baths,
      sqft: form.sqft === '' ? null : Number(form.sqft),
      availableFrom: form.available || null,
      description: form.description.trim() || undefined,
      amenities: form.amenities,
      photos,
      coverPhoto: photos[0] ?? null,
      // An agent's new submission is always inactive until an admin activates it.
      status: submitsForReview ? 'inactive' : form.status,
      // Rental-only fields. Backend nulls these out for Sale, but no harm in sending.
      petPolicy: form.type === 'Rental' ? (form.petPolicy as ListingInput['petPolicy']) : null,
      leaseTerm: form.type === 'Rental' ? (form.lease as ListingInput['leaseTerm']) : null,
    };

    setSubmitting(true);
    try {
      const saved =
        isEditing && editingId !== null
          ? await updateListing(editingId, payload)
          : await createListing(payload);
      toast(
        isEditing
          ? `Listing "${saved.title}" updated`
          : submitsForReview
            ? `Listing "${saved.title}" submitted for admin review`
            : `Listing "${saved.title}" saved as ${saved.status}`,
        'success'
      );
      navigate(listingsPath);
    } catch (err) {
      toast(extractApiError(err, 'Failed to save listing'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Edit-mode loading / error states (shown before the form mounts) ─
  if (isEditing && loadingExisting) {
    return (
      <div
        className="card text-center text-g500"
        style={{ padding: '64px 32px', borderRadius: 16 }}
      >
        <Icon name="building-2" size={28} color="var(--g400)" /> Loading
        listing...
      </div>
    );
  }
  if (isEditing && loadError) {
    return (
      <div
        className="card"
        style={{
          padding: 24,
          borderRadius: 16,
          background: 'var(--red-bg)',
          color: 'var(--red)',
          border: '1px solid rgba(220,38,38,.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load this listing</strong>
        </div>
        <div className="text-sm mb-3">{loadError}</div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(listingsPath)}
        >
          Back to listings
        </button>
      </div>
    );
  }

  // ── Create-mode quota gate — runs BEFORE Step 1 is shown ─────────────
  if (!isEditing && checkingQuota) {
    return (
      <div
        className="card text-center text-g500"
        style={{ padding: '64px 32px', borderRadius: 16 }}
      >
        <Icon name="building-2" size={28} color="var(--g400)" /> Checking
        your plan...
      </div>
    );
  }

  if (!isEditing && quotaError) {
    return (
      <div
        className="card"
        style={{
          padding: 24,
          borderRadius: 16,
          background: 'var(--red-bg)',
          color: 'var(--red)',
          border: '1px solid rgba(220,38,38,.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't check your plan limit</strong>
        </div>
        <div className="text-sm mb-3">{quotaError}</div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!isEditing && quota && !quota.canCreate) {
    // At-or-over-cap block screen. Shown instead of Step 1.
    return (
      <div
        className="card animate-fadeIn px-5 py-8 sm:px-8 sm:py-10"
        style={{ borderRadius: 16 }}
      >
        <div className="flex flex-col items-center text-center">
          <div
            className="rounded-[20px] flex items-center justify-center mb-5"
            style={{ width: 72, height: 72, background: 'var(--brand-bg)' }}
          >
            <Icon name="credit-card" size={32} color="var(--brand)" />
          </div>
          <h2
            className="font-extrabold text-ink mb-2"
            style={{ fontSize: 22, letterSpacing: '-0.3px' }}
          >
            You've hit your listing limit
          </h2>
          <p
            className="text-g600 mb-5 leading-relaxed"
            style={{ fontSize: 14, maxWidth: 480 }}
          >
            Your <strong>{quota.planName}</strong> plan allows up to{' '}
            <strong>{quota.limit}</strong>{' '}
            {quota.limit === 1 ? 'listing' : 'listings'}, and you currently
            have <strong>{quota.used}</strong>. Upgrade your subscription to
            publish more.
          </p>

          {/* Compact usage meter */}
          <div
            className="w-full mb-6"
            style={{ maxWidth: 360, background: 'var(--g100)', borderRadius: 100, height: 8 }}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                borderRadius: 100,
                background: 'var(--brand)',
              }}
            />
          </div>

          <div className="flex gap-2.5 flex-wrap justify-center">
            <button
              className="btn btn-secondary"
              onClick={() => navigate(listingsPath)}
            >
              Back to my listings
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/agent/subscription')}
            >
              <Icon name="trending-up" size={14} color="#fff" /> Upgrade my plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card px-4 py-5 sm:px-8 sm:py-7" style={{ borderRadius: 16 }}>
      {/* Header — varies by mode */}
      <div className="mb-5">
        <h2
          className="font-extrabold text-ink text-xl sm:text-[22px]"
          style={{ letterSpacing: '-0.3px' }}
        >
          {isEditing ? 'Edit listing' : 'Add a new listing'}
        </h2>
        <p className="text-g500 mt-1" style={{ fontSize: 13 }}>
          {isEditing
            ? 'Update the details of your existing listing.'
            : 'Walk through 4 steps to publish your property.'}
        </p>

        {/* Quota indicator (create mode only, when a finite limit exists) */}
        {!isEditing && quota && !quota.unlimited && quota.limit !== null && (
          <div
            className="mt-3 flex items-center gap-3 px-3.5 py-2 rounded-xl"
            style={{
              background: 'var(--brand-bg)',
              border: '1px solid rgba(225,1,116,0.12)',
              maxWidth: 420,
            }}
          >
            <Icon name="package" size={16} color="var(--brand)" />
            <div className="text-[13px] text-g700">
              <strong className="text-ink">{quota.planName} plan:</strong>{' '}
              {quota.used}/{quota.limit} listings used (
              {quota.remaining} {quota.remaining === 1 ? 'slot' : 'slots'}{' '}
              left)
            </div>
          </div>
        )}
      </div>

      {/* Step bar */}
      <div className="flex items-center justify-center mb-6 sm:mb-8 px-0 sm:px-5">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const done = n < step;
          const current = n === step;
          return (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center">
                <div
                  className="rounded-full flex items-center justify-center font-bold flex-shrink-0"
                  style={{
                    width: 36,
                    height: 36,
                    fontSize: 14,
                    border: '2px solid ' + (done || current ? 'var(--brand)' : 'var(--g300)'),
                    background: done ? 'var(--brand)' : '#fff',
                    color: done ? '#fff' : current ? 'var(--brand)' : 'var(--g400)',
                    boxShadow: current ? '0 0 0 4px rgba(225,1,116,0.12)' : undefined,
                    transition: 'all .2s ease',
                  }}
                >
                  {done ? <Icon name="check-circle" size={16} color="#fff" /> : n}
                </div>
                <div
                  className="font-semibold text-center mt-1.5"
                  style={{
                    fontSize: 11,
                    color: done ? 'var(--green)' : current ? 'var(--brand)' : 'var(--g400)',
                    maxWidth: 80,
                  }}
                >
                  {label}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    maxWidth: 80,
                    height: 2,
                    background: done ? 'var(--brand)' : 'var(--g200)',
                    margin: '0 4px',
                    marginTop: -22,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div>
        {/* Step 1: Basics */}
        {step === 1 && (
          <div>
            <h3 className="font-bold mb-1" style={{ fontSize: 18 }}>
              Step 1 — Basic Info
            </h3>
            <p className="text-g500 mb-5" style={{ fontSize: 13 }}>
              Tell us about your property.
            </p>

            <div className="mb-4">
              <label className="form-label">
                Listing Title <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <input
                className="form-input"
                style={errors.title ? { borderColor: 'var(--red)' } : undefined}
                placeholder="e.g. Modern 2BR Apartment in Midtown"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
              />
              {errors.title && (
                <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                  {errors.title}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="form-label">
                Full Address <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <input
                className="form-input"
                style={errors.address ? { borderColor: 'var(--red)' } : undefined}
                placeholder="1425 Woodward Ave, Detroit, MI"
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
              />
              {errors.address && (
                <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                  {errors.address}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-3">
              <div className="mb-4">
                <label className="form-label">Listing Type</label>
                <select
                  className="form-input"
                  value={form.type}
                  onChange={(e) => set('type', e.target.value as 'Rental' | 'Sale')}
                >
                  <option value="Rental">Rental</option>
                  <option value="Sale">Buy</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="form-label">
                  Price <span style={{ color: 'var(--red)' }}>*</span>
                  {form.type === 'Rental' && (
                    <span className="text-g400 font-normal"> ($/month)</span>
                  )}
                </label>
                <input
                  type="number"
                  min="0"
                  className="form-input"
                  style={errors.price ? { borderColor: 'var(--red)' } : undefined}
                  placeholder={form.type === 'Rental' ? '1450' : '185000'}
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                />
                {errors.price && (
                  <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                    {errors.price}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="mb-4">
                <label className="form-label">Bedrooms</label>
                <div className="inline-flex border border-g200 overflow-hidden" style={{ borderRadius: 10 }}>
                  <button type="button" onClick={() => set('beds', Math.max(0, form.beds - 1))}
                    style={{ width: 38, height: 38, background: 'var(--g50)', color: 'var(--g600)', fontSize: 18, border: 'none', cursor: 'pointer' }}>−</button>
                  <input readOnly value={form.beds} className="text-center font-semibold"
                    style={{ width: 48, height: 38, borderLeft: '1px solid var(--g200)', borderRight: '1px solid var(--g200)', fontSize: 15 }} />
                  <button type="button" onClick={() => set('beds', form.beds + 1)}
                    style={{ width: 38, height: 38, background: 'var(--g50)', color: 'var(--g600)', fontSize: 18, border: 'none', cursor: 'pointer' }}>+</button>
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label">Bathrooms</label>
                <div className="inline-flex border border-g200 overflow-hidden" style={{ borderRadius: 10 }}>
                  <button type="button" onClick={() => set('baths', Math.max(0, form.baths - 1))}
                    style={{ width: 38, height: 38, background: 'var(--g50)', color: 'var(--g600)', fontSize: 18, border: 'none', cursor: 'pointer' }}>−</button>
                  <input readOnly value={form.baths} className="text-center font-semibold"
                    style={{ width: 48, height: 38, borderLeft: '1px solid var(--g200)', borderRight: '1px solid var(--g200)', fontSize: 15 }} />
                  <button type="button" onClick={() => set('baths', form.baths + 1)}
                    style={{ width: 38, height: 38, background: 'var(--g50)', color: 'var(--g600)', fontSize: 18, border: 'none', cursor: 'pointer' }}>+</button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-3">
              <div className="mb-4">
                <label className="form-label">Square Footage</label>
                <input
                  type="number"
                  min="0"
                  className="form-input"
                  style={errors.sqft ? { borderColor: 'var(--red)' } : undefined}
                  placeholder="950"
                  value={form.sqft}
                  onChange={(e) => set('sqft', e.target.value)}
                />
                {errors.sqft && (
                  <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                    {errors.sqft}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="form-label">Available From</label>
                <input
                  type="date"
                  className="form-input"
                  style={errors.available ? { borderColor: 'var(--red)' } : undefined}
                  value={form.available}
                  onChange={(e) => set('available', e.target.value)}
                />
                {errors.available && (
                  <div className="text-[12px] mt-1" style={{ color: 'var(--red)' }}>
                    {errors.available}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button className="btn btn-primary" onClick={() => goToStep(2)}>
                Continue <Icon name="chevron-right" size={14} color="#fff" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div>
            <h3 className="font-bold mb-1" style={{ fontSize: 18 }}>
              Step 2 — Details
            </h3>
            <p className="text-g500 mb-5" style={{ fontSize: 13 }}>
              Describe your property and select amenities.
            </p>

            <div className="mb-4">
              <label className="form-label">
                Description <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <textarea
                rows={4}
                maxLength={1000}
                className="form-input"
                style={errors.description ? { borderColor: 'var(--red)' } : undefined}
                placeholder="Bright and spacious 2-bedroom apartment in the heart of..."
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
              <div className="flex justify-between" style={{ marginTop: 4 }}>
                {errors.description ? (
                  <div className="text-[12px]" style={{ color: 'var(--red)' }}>
                    {errors.description}
                  </div>
                ) : (
                  <span />
                )}
                <div className="text-right text-g400" style={{ fontSize: 11 }}>
                  {form.description.length}/1000
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label">
                Amenities <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {AMENITIES.map((a) => {
                  const sel = form.amenities.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => {
                        toggleAmenity(a);
                        if (errors.amenities) {
                          setErrors((e) => {
                            const { amenities, ...rest } = e;
                            return rest;
                          });
                        }
                      }}
                      className="flex items-center gap-1.5"
                      style={{
                        padding: '7px 14px',
                        fontSize: 12.5,
                        fontWeight: sel ? 600 : 500,
                        border: '1px solid ' + (sel ? 'var(--brand)' : 'var(--g200)'),
                        background: sel ? 'var(--brand-light)' : '#fff',
                        color: sel ? 'var(--brand)' : 'var(--g600)',
                        borderRadius: 8,
                        cursor: 'pointer',
                      }}
                    >
                      {sel && <Icon name="check-circle" size={12} color="var(--brand)" />}
                      {a}
                    </button>
                  );
                })}
              </div>
              {errors.amenities && (
                <div className="text-[12px] mt-2" style={{ color: 'var(--red)' }}>
                  {errors.amenities}
                </div>
              )}
            </div>

            {form.type === 'Rental' && (
              <>
                <div className="mb-4">
                  <label className="form-label">Pet Policy</label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { v: 'allowed',     l: 'Pets Allowed' },
                      { v: 'not-allowed', l: 'No Pets' },
                      { v: 'cats-only',   l: 'Cats Only' },
                      { v: 'dogs-only',   l: 'Dogs Only' },
                    ].map((o) => {
                      const on = form.petPolicy === o.v;
                      return (
                        <button key={o.v} type="button" onClick={() => set('petPolicy', o.v)}
                          style={{
                            padding: '8px 16px',
                            fontSize: 13,
                            fontWeight: on ? 600 : 500,
                            border: '1px solid ' + (on ? 'var(--brand)' : 'var(--g200)'),
                            background: on ? 'var(--brand-light)' : '#fff',
                            color: on ? 'var(--brand)' : 'var(--g700)',
                            borderRadius: 10,
                            cursor: 'pointer',
                          }}>
                          {o.l}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label">Lease Term</label>
                  <select className="form-input" style={{ maxWidth: 300 }}
                    value={form.lease} onChange={(e) => set('lease', e.target.value)}>
                    <option value="month-to-month">Month-to-Month</option>
                    <option value="6-months">6 Months</option>
                    <option value="1-year">1 Year</option>
                    <option value="2-years">2 Years</option>
                  </select>
                </div>
              </>
            )}

            <div className="flex justify-between mt-4">
              <button className="btn btn-secondary" onClick={() => goToStep(1)}>Back</button>
              <button className="btn btn-primary" onClick={() => goToStep(3)}>
                Continue <Icon name="chevron-right" size={14} color="#fff" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Photos */}
        {step === 3 && (
          <div>
            <h3 className="font-bold mb-1" style={{ fontSize: 18 }}>
              Step 3 — Photos
            </h3>
            <p className="text-g500 mb-5" style={{ fontSize: 13 }}>
              Upload at least one photo. The first photo becomes the cover.
            </p>

            {/* Hidden native file input — opened programmatically. */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              style={{ display: 'none' }}
              onChange={onInputChange}
            />

            <div
              role="button"
              tabIndex={0}
              className="flex flex-col items-center text-center gap-2 cursor-pointer mb-2 p-6 sm:p-10"
              style={{
                border:
                  '2px dashed ' +
                  (errors.photos ? 'var(--red)' : 'var(--g300)'),
                borderRadius: 16,
                background: uploading ? 'var(--brand-bg)' : 'var(--g50)',
                opacity: uploading ? 0.85 : 1,
                transition: 'background .15s, opacity .15s',
              }}
              onClick={onPickFiles}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onPickFiles();
              }}
              onDrop={onDrop}
              onDragOver={onDragOver}
            >
              <div
                className="rounded-2xl flex items-center justify-center"
                style={{ width: 56, height: 56, background: 'var(--brand-bg)' }}
              >
                <Icon
                  name={uploading ? 'package' : 'plus'}
                  size={26}
                  color="var(--brand)"
                />
              </div>
              <div className="font-semibold text-ink" style={{ fontSize: 15 }}>
                {uploading ? (
                  'Uploading...'
                ) : (
                  <>
                    <span className="sm:hidden">Tap to add photos</span>
                    <span className="hidden sm:inline">
                      Drag photos here or click to browse
                    </span>
                  </>
                )}
              </div>
              <div className="text-g500" style={{ fontSize: 13 }}>
                JPG, PNG, WEBP, GIF — max 5MB each · maximum {MAX_PHOTOS}{' '}
                photos ({photos.length}/{MAX_PHOTOS} used)
              </div>
            </div>

            {errors.photos && (
              <div
                className="text-[12px] mb-3"
                style={{ color: 'var(--red)' }}
              >
                {errors.photos}
              </div>
            )}

            {photos.length > 0 && (
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}
              >
                {photos.map((url, i) => (
                  <div
                    key={url + i}
                    className="relative overflow-hidden"
                    style={{
                      aspectRatio: '1',
                      borderRadius: 12,
                      background: 'var(--g100)',
                      border: '1px solid var(--g200)',
                    }}
                  >
                    <img
                      src={url}
                      alt={`Listing photo ${i + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    {i === 0 && (
                      <div
                        className="absolute font-bold text-white"
                        style={{
                          top: 6,
                          left: 6,
                          fontSize: 10,
                          padding: '2px 7px',
                          borderRadius: 6,
                          background: 'var(--brand)',
                        }}
                      >
                        Cover
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute w-7 h-7 sm:w-[22px] sm:h-[22px]"
                      aria-label="Remove photo"
                      style={{
                        top: 6,
                        right: 6,
                        borderRadius: '50%',
                        background: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        border: 'none',
                        fontSize: 13,
                        cursor: 'pointer',
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between mt-5">
              <button
                className="btn btn-secondary"
                onClick={() => goToStep(2)}
                disabled={uploading}
              >
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={() => goToStep(4)}
                disabled={uploading}
              >
                Continue <Icon name="chevron-right" size={14} color="#fff" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div>
            <h3 className="font-bold mb-1" style={{ fontSize: 18 }}>
              {isEditing
                ? 'Step 4 — Review & Save'
                : submitsForReview
                  ? 'Step 4 — Review & Submit'
                  : 'Step 4 — Review & Publish'}
            </h3>
            <p className="text-g500 mb-5" style={{ fontSize: 13 }}>
              {isEditing
                ? 'Double-check your changes before saving.'
                : submitsForReview
                  ? 'Double-check everything before sending it for review.'
                  : 'Double-check everything before going live.'}
            </p>

            <div className="mb-4" style={{ background: 'var(--g50)', borderRadius: 12, padding: 16 }}>
              <div className="font-bold mb-2" style={{ fontSize: 14 }}>{form.title || '(No title)'}</div>
              <div className="text-g500 mb-3" style={{ fontSize: 13 }}>{form.address || '(No address)'}</div>
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', fontSize: 13 }}>
                <div><strong>Type:</strong> {listingTypeLabel(form.type)}</div>
                <div><strong>Price:</strong> ${form.price || '0'}{form.type === 'Rental' ? '/mo' : ''}</div>
                <div><strong>Beds/Baths:</strong> {form.beds} / {form.baths}</div>
                <div><strong>Sq Ft:</strong> {form.sqft || '—'}</div>
                <div><strong>Available:</strong> {form.available || '—'}</div>
                <div><strong>Photos:</strong> {photos.length}</div>
              </div>
            </div>

            {form.amenities.length > 0 && (
              <div className="mb-4">
                <div className="font-semibold text-g400 uppercase mb-2" style={{ fontSize: 11, letterSpacing: '.6px' }}>Amenities</div>
                <div className="flex flex-wrap gap-1.5">
                  {form.amenities.map((a) => (
                    <span key={a} style={{
                      fontSize: 11.5, padding: '3px 10px', borderRadius: 100,
                      background: 'var(--brand-bg)', color: 'var(--brand)', fontWeight: 600,
                    }}>{a}</span>
                  ))}
                </div>
              </div>
            )}

            {submitsForReview ? (
              <div
                className="mb-4 flex items-start gap-3"
                style={{
                  background: 'var(--brand-bg)', borderRadius: 12, padding: '14px 16px',
                }}
              >
                <Icon name="shield" size={16} color="var(--brand)" />
                <div style={{ fontSize: 13 }}>
                  <div className="font-bold mb-0.5" style={{ color: 'var(--brand)' }}>
                    Goes to an admin for review
                  </div>
                  <span className="text-g500">
                    Your listing is saved as inactive and stays hidden from renters
                    until an admin approves it. You'll get a notification when it goes live.
                  </span>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <label className="form-label">Publish status</label>
                <div className="flex items-center gap-3">
                  <Toggle on={form.status === 'active'}
                    onChange={() => set('status', form.status === 'active' ? 'inactive' : 'active')} />
                  <span className="font-semibold" style={{
                    fontSize: 14,
                    color: form.status === 'active' ? 'var(--green)' : 'var(--g500)',
                  }}>
                    {form.status === 'active' ? 'Active (visible to public)' : 'Inactive (hidden)'}
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-between mt-5">
              <button
                className="btn btn-secondary"
                onClick={() => goToStep(3)}
                disabled={submitting}
              >
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={publish}
                disabled={submitting}
              >
                {submitting ? (
                  isEditing ? 'Saving...' : submitsForReview ? 'Submitting...' : 'Publishing...'
                ) : (
                  <>
                    <Icon name="check-circle" size={14} color="#fff" />{' '}
                    {isEditing
                      ? 'Save Changes'
                      : submitsForReview
                        ? 'Submit for Review'
                        : 'Publish Listing'}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentAddListing;
